<template>
  <div class="employee-page">
    <HeaderBar />

    <main class="content">
      <el-card v-if="loading" class="step-card" shadow="hover">
        <el-skeleton animated :rows="6" />
      </el-card>

      <el-card v-else class="step-card" shadow="hover">
        <div class="step-copy">
          <p class="step-label">{{ isDenied ? 'Verification Update Needed' : 'Step 3 of 3' }}</p>
          <h1>{{ headline }}</h1>
          <p class="step-text">{{ summary }}</p>
        </div>

        <el-alert
          class="status-alert"
          :type="isDenied ? 'error' : 'info'"
          :closable="false"
          :title="alertTitle"
        />

        <div v-if="isDenied" class="review-card">
          <p class="review-label">Owner comment</p>
          <p class="review-comment">{{ denialComment || 'No denial comment was provided.' }}</p>
        </div>

        <div class="actions">
          <el-button
            v-if="isDenied"
            type="primary"
            size="large"
            :loading="retrying"
            @click="restartVerification"
          >
            Refill background verification
          </el-button>
          <el-button size="large" @click="goBackToPortals">
            Back to portals
          </el-button>
          <el-button size="large" @click="logout">
            Log out
          </el-button>
        </div>
      </el-card>
    </main>
  </div>
</template>

<script>
import HeaderBar from '@/components/HeaderBar.vue'
import { auth } from '@/firebase'
import {
  EMPLOYEE_PORTAL_STATUSES,
  resolveUserProfile,
} from '@/services/userProfile'
import { ElMessage } from 'element-plus'
import { signOut } from 'firebase/auth'
import { onSnapshot } from 'firebase/firestore'

export default {
  name: 'EmployeePendingApprovalView',
  components: { HeaderBar },
  data() {
    return {
      loading: true,
      retrying: false,
      status: EMPLOYEE_PORTAL_STATUSES.PENDING_APPROVAL,
      denialComment: '',
      unsub: null,
    }
  },
  computed: {
    isDenied() {
      return this.status === EMPLOYEE_PORTAL_STATUSES.DENIED
    },
    headline() {
      return this.isDenied
        ? 'Your background verification needs updates'
        : 'Verification in progress'
    },
    summary() {
      return this.isDenied
        ? 'Review the owner feedback below, then restart the background verification step and upload the corrected documents.'
        : 'Your employee verification and form submission are under manual review. Once the owner finishes reviewing them, your portal access will be updated.'
    },
    alertTitle() {
      return this.isDenied
        ? 'Your submission was denied. Update your verification files and submit them again.'
        : 'Your onboarding request is waiting for owner approval.'
    },
  },
  async mounted() {
    await this.subscribeToProfile()
  },
  beforeUnmount() {
    this.unsub && this.unsub()
  },
  methods: {
    async subscribeToProfile() {
      const user = auth.currentUser
      if (!user) {
        this.$router.push({ name: 'Login' })
        return
      }

      const profile = await resolveUserProfile(user).catch(() => null)
      if (!profile?.ref) {
        this.loading = false
        return
      }

      this.unsub && this.unsub()
      this.unsub = onSnapshot(
        profile.ref,
        (docSnap) => {
          const data = docSnap.data() || {}
          const employeePortal = data.employeePortal || {}
          const nextStatus = employeePortal.status || EMPLOYEE_PORTAL_STATUSES.PENDING_APPROVAL

          this.status = nextStatus
          this.denialComment = String(employeePortal.denialComment || '').trim()
          this.loading = false

          if (nextStatus === EMPLOYEE_PORTAL_STATUSES.APPROVED) {
            this.$router.replace({ name: 'Dashboard' })
          } else if (nextStatus === EMPLOYEE_PORTAL_STATUSES.FORM_REQUIRED) {
            this.$router.replace({ name: 'EmployeeFormStep' })
          } else if (nextStatus === EMPLOYEE_PORTAL_STATUSES.VERIFICATION_REQUIRED) {
            this.$router.replace({ name: 'EmployeeVerification' })
          }
        },
        () => {
          this.loading = false
        }
      )
    },
    async restartVerification() {
      if (this.retrying) return

      const user = auth.currentUser
      if (!user) {
        this.$router.push({ name: 'Login' })
        return
      }

      this.retrying = true
      try {
        await this.$router.push({
          name: 'EmployeeVerification',
          query: { retry: '1' },
        })
      } catch (error) {
        ElMessage({
          type: 'error',
          message: error?.message || 'Unable to open the verification step right now.',
          showClose: true,
          duration: 5000,
        })
      } finally {
        this.retrying = false
      }
    },
    goBackToPortals() {
      this.$router.replace({ name: 'CompleteProfile' })
    },
    async logout() {
      try {
        await signOut(auth)
      } catch (e) {}
      this.$router.push({ name: 'Login' })
    },
  },
}
</script>

<style scoped>
.employee-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f6fbff 0%, #e9f2ff 100%);
  color: #03241f;
}

.content {
  max-width: 820px;
  margin: 36px auto 64px;
  padding: 0 16px;
  box-sizing: border-box;
}

.step-card {
  border-radius: 22px;
  border: 1px solid rgba(3, 36, 31, 0.1);
  box-shadow: 0 20px 48px rgba(3, 36, 31, 0.12);
}

.step-copy {
  margin-bottom: 20px;
}

.step-label {
  margin: 0 0 8px;
  color: #0b6cff;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.step-copy h1 {
  margin: 0 0 8px;
  font-size: 2rem;
  font-weight: 800;
}

.step-text {
  margin: 0;
  color: #486a64;
  font-size: 1rem;
}

.status-alert {
  margin-bottom: 24px;
}

.review-card {
  border: 1px solid rgba(180, 35, 24, 0.14);
  border-radius: 18px;
  padding: 20px;
  background: #fff8f7;
}

.review-label {
  margin: 0 0 10px;
  color: #b42318;
  font-size: 0.85rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.review-comment {
  margin: 0;
  color: #03241f;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.6;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
}

@media (max-width: 600px) {
  .content {
    margin-top: 28px;
  }

  .step-copy h1 {
    font-size: 1.7rem;
  }

  .actions {
    flex-direction: column;
  }
}
</style>
